import { StarIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { cn } from '@/lib/utils'

interface StarRatingProps {
  rating: number
  maxRating?: number
  size?: 'sm' | 'md' | 'lg'
  showValue?: boolean
  className?: string
}

const sizeClassMap: Record<NonNullable<StarRatingProps['size']>, string> = {
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-5',
}

const textSizeClassMap: Record<NonNullable<StarRatingProps['size']>, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
}

export const StarRating = ({
  rating,
  maxRating = 5,
  size = 'md',
  showValue = false,
  className,
}: StarRatingProps): ReactNode => {
  const clampedRating = Math.max(0, Math.min(rating, maxRating))
  const fullStarCount = Math.floor(clampedRating)
  const hasHalfStar = clampedRating % 1 >= 0.5
  const emptyStarCount = maxRating - fullStarCount - (hasHalfStar ? 1 : 0)

  const fullStarKeyList = useMemo(
    () => Array.from({ length: fullStarCount }, (_, i) => `full-star-${i}`),
    [fullStarCount]
  )

  const emptyStarKeyList = useMemo(
    () => Array.from({ length: emptyStarCount }, (_, i) => `empty-star-${i}`),
    [emptyStarCount]
  )

  const sizeClass = sizeClassMap[size]
  const textSizeClass = textSizeClassMap[size]

  return (
    <div className={cn('star-rating flex items-center gap-1', className)}>
      <div className="star-rating-stars flex items-center">
        {fullStarKeyList.map((key) => (
          <StarIcon key={key} className={cn(sizeClass, 'fill-yellow-400 text-yellow-400')} />
        ))}
        {hasHalfStar && (
          <div className="relative">
            <StarIcon className={cn(sizeClass, 'text-muted-foreground/30')} />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon className={cn(sizeClass, 'fill-yellow-400 text-yellow-400')} />
            </div>
          </div>
        )}
        {emptyStarKeyList.map((key) => (
          <StarIcon key={key} className={cn(sizeClass, 'text-muted-foreground/30')} />
        ))}
      </div>
      {showValue && (
        <span className={cn('star-rating-value text-muted-foreground', textSizeClass)}>
          {clampedRating.toFixed(1)}
        </span>
      )}
    </div>
  )
}
